// @tennis-matches/services/trainerstunden/billing-contacts

import { getSupabaseClient } from "../supabase/index.js";

const FALLBACK_KEY = "ttb_trainerstunden_billing_contacts_fallback_v1";

function isMissingTableError(error) {
  const code = String(error?.code || "");
  const msg = String(error?.message || error?.details || "");
  return code === "42P01" || /trainerstunden_billing_contacts/i.test(msg);
}

function readFallback() {
  try {
    const raw = localStorage.getItem(FALLBACK_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (_) {
    return [];
  }
}

function writeFallback(arr) {
  try {
    localStorage.setItem(FALLBACK_KEY, JSON.stringify(Array.isArray(arr) ? arr : []));
  } catch (_) {
    // noop
  }
}

export async function loadBillingContacts() {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const { data, error } = await supabase
      .from("trainerstunden_billing_contacts")
      .select("*")
      .order("last_name", { ascending: true })
      .order("first_name", { ascending: true });
    if (error) {
      if (isMissingTableError(error)) return { ok: true, data: readFallback() };
      return { ok: false, error };
    }
    return { ok: true, data: data || [] };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function createBillingContact(contact) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const { data, error } = await supabase
      .from("trainerstunden_billing_contacts")
      .insert([contact])
      .select()
      .maybeSingle();
    if (error) {
      if (isMissingTableError(error)) {
        const next = [...readFallback(), contact];
        writeFallback(next);
        return { ok: true, data: contact };
      }
      return { ok: false, error };
    }
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function updateBillingContact(id, updates) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const { data, error } = await supabase
      .from("trainerstunden_billing_contacts")
      .update(updates)
      .eq("id", id)
      .select()
      .maybeSingle();
    if (error) {
      if (isMissingTableError(error)) {
        const next = readFallback().map((x) => (x.id === id ? { ...x, ...updates } : x));
        writeFallback(next);
        return { ok: true, data: next.find((x) => x.id === id) || { id, ...updates } };
      }
      return { ok: false, error };
    }
    return { ok: true, data: data || { id, ...updates } };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function deleteBillingContact(id) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const { error } = await supabase
      .from("trainerstunden_billing_contacts")
      .delete()
      .eq("id", id);
    if (error) {
      if (isMissingTableError(error)) {
        writeFallback(readFallback().filter((x) => x.id !== id));
        return { ok: true };
      }
      return { ok: false, error };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e };
  }
}
